import React, { useState, useEffect } from 'react';
import Analytics from '../services/analytics';
import '../css/quiz.css';

interface QuizQuestion {
  id: number;
  labelText: string;
  question: string;
  options: string[];
  correctAnswer: string;
  explanation: string;
}

interface LabelQuizProps {
  questions: QuizQuestion[];
}

const LabelQuiz: React.FC<LabelQuizProps> = ({ questions }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [showExplanation, setShowExplanation] = useState(false);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [quizComplete, setQuizComplete] = useState(false);
  const [quizStarted, setQuizStarted] = useState(false);

  const currentQ = questions[currentQuestion];

  useEffect(() => {
    // Track when quiz is first interacted with
    if (!quizStarted && selectedAnswer) {
      setQuizStarted(true);
      Analytics.trackQuizEvent('Label Reading Quiz', 'quiz_start');
    }
  }, [selectedAnswer, quizStarted]);

  const handleAnswerSelect = (answer: string) => {
    setSelectedAnswer(answer);
  };

  const handleSubmit = () => {
    if (!showExplanation) {
      setShowExplanation(true);
      if (selectedAnswer === currentQ.correctAnswer) {
        setCorrectAnswers(correctAnswers + 1);
      }
    } else {
      if (currentQuestion === questions.length - 1) {
        setQuizComplete(true);
        // Track quiz completion
        Analytics.trackQuizEvent(
          'Label Reading Quiz',
          'quiz_complete',
          Math.round((correctAnswers / questions.length) * 100)
        );
      } else {
        setCurrentQuestion(currentQuestion + 1);
        setSelectedAnswer('');
        setShowExplanation(false);
      }
    }
  };

  const getFinalMessage = () => {
    const score = correctAnswers / questions.length;
    if (score === 1) {
      return "Perfect! You're ready to read any medication label that comes your way.";
    } else if (score >= 0.67) {
      return "Good work, but there's still room for improvement. Review the guide and try again to master label reading.";
    } else {
      return "These formats can be tricky! Take another look at the guide above and try again - it's important to get this right.";
    }
  };

  const handleTryAgain = () => {
    // Track quiz restart
    Analytics.trackQuizEvent('Label Reading Quiz', 'quiz_restart');
    setCurrentQuestion(0);
    setSelectedAnswer('');
    setShowExplanation(false);
    setCorrectAnswers(0);
    setQuizComplete(false);
    setQuizStarted(false);
  };

  if (quizComplete) {
    return (
      <div className="quiz-results">
        <h3>Quiz Complete!</h3>
        <p>
          You got {correctAnswers} out of {questions.length} correct.
        </p>
        <p>{getFinalMessage()}</p>
        <button onClick={handleTryAgain} className="quiz-button">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="label-quiz">
      <div className="question-container">
        <h3>
          Question {currentQuestion + 1} of {questions.length}
        </h3>
        <div className="label-example">
          <pre>{currentQ.labelText}</pre>
        </div>
        <p className="question-text">{currentQ.question}</p>
      </div>

      <div className="options-container">
        {currentQ.options.map((option, index) => (
          <button
            key={index}
            className={`option-button ${selectedAnswer === option ? 'selected' : ''} ${
              showExplanation ? (option === currentQ.correctAnswer ? 'correct' : 'incorrect') : ''
            }`}
            onClick={() => handleAnswerSelect(option)}
            disabled={showExplanation}
          >
            {option}
          </button>
        ))}
      </div>

      {showExplanation && (
        <div className={`explanation ${selectedAnswer === currentQ.correctAnswer ? 'correct' : 'incorrect'}`}>
          <p>
            <strong>{selectedAnswer === currentQ.correctAnswer ? '✓ Correct!' : '✗ Incorrect'}</strong>
          </p>
          <p>{currentQ.explanation}</p>
        </div>
      )}

      <button onClick={handleSubmit} disabled={!selectedAnswer} className="quiz-button">
        {showExplanation
          ? currentQuestion === questions.length - 1
            ? 'See Results'
            : 'Next Question'
          : 'Check Answer'}
      </button>
    </div>
  );
};

export default LabelQuiz;
